import { useState, useRef } from "react";
import { useQueryClient, useMutation } from "react-query";
import { useTranslation } from "react-i18next";
import { Alert, AlertType } from "@components/new/Alert";
import { Modal, ModalSize } from "@components/new/Modal";
import Stepper, { EStepState } from "@components/new/Stepper";
import { MessageModalContent } from "@components/modals/MessageModalNew";
import ConfirmModal from "@components/modals/ConfirmModal";
import {
  getIfMatchingDeletionAvailable,
  removeFBPMatching,
  TFBPMatchingItem,
} from "@api/apiClient/fbp";
import Form from "./Form";
import DelIcon2 from "@assets/icons/solid/trash-fill.svg?react";
import { FormHandle } from "./types";
import styles from "./Form.module.scss";

interface OrderModalProps {
  onClose: () => void;
  sellerWarehouseId: string;
  hasAliStores: boolean;
  hasWbStores: boolean;
  hasYmStores: boolean;
  matchingData?: TFBPMatchingItem;
}

interface ISubmitButtonProps {
  disabled: boolean;
  isLoading?: boolean;
  alert?: React.ReactNode;
}

enum steps {
  FORM = "form",
  SUCCESS = "success",
  ERROR = "error",
}

const MatchingModal = ({
  onClose,
  sellerWarehouseId,
  hasAliStores,
  hasWbStores,
  hasYmStores,
  matchingData,
}: OrderModalProps) => {
  const [accordeonPage, setAccordeonPage] = useState<string>("marketplaces");
  const [step, setStep] = useState(steps.FORM);
  const [isOpenDelWindow, setIsOpenDelWindow] = useState<boolean>(false);
  const [buttonProps, setButtonProps] = useState<ISubmitButtonProps>({
    disabled: false,
    isLoading: false,
    alert: null,
  });
  const { t } = useTranslation();
  const formRef = useRef<FormHandle>({ submit: () => {} });
  const queryClient = useQueryClient();

  const matchingId = matchingData?.id ?? "";

  const actionAlert = (
    <Alert
      type={AlertType.ERROR}
      title={t("fbpPage.matchings.activeActionsAlert")}
    />
  );

  const checkDelMutation = useMutation({
    mutationFn: () => getIfMatchingDeletionAvailable({ id: matchingId }),
    onSuccess: (res) => {
      if (res?.isAvailable) {
        setIsOpenDelWindow(true);
      } else {
        handleStatusChange({
          ...buttonProps,
          alert: actionAlert,
        });
      }
    },
    onError: () => {
      handleStatusChange({
        ...buttonProps,
        alert: actionAlert,
      });
    },
  });

  const delMutation = useMutation({
    mutationFn: (id: string) => removeFBPMatching(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getFBPMatchings"] });
    },
  });

  const handleStatusChange = (props: ISubmitButtonProps) => {
    setButtonProps({
      disabled: props.disabled ?? false,
      isLoading: props.isLoading ?? false,
      alert: props.alert || null,
    });
  };

  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["getFBPMatchings"] });
    setStep(steps.SUCCESS);
  };

  const onError = () => {
    setStep(steps.ERROR);
  };

  const title = matchingData
    ? t("fbpPage.matchings.modalTitle")
    : t("fbpPage.matchings.modalTitleNew");

  const marketplacesAvailable: string[] = [];
  if (hasWbStores) marketplacesAvailable.push("Wildberries");
  if (hasYmStores) marketplacesAvailable.push("Yandex");
  if (hasAliStores) marketplacesAvailable.push("Aliexpress");

  const stepsData = matchingData ? [
    {
      text: t("fbpPage.matchings.step-edit-1"),
      state: step === steps.SUCCESS
        ? EStepState.SUCCESS
        : EStepState.ENABLED
    },
    {
      text: t("fbpPage.matchings.step-edit-2"),
      state:
        step === steps.SUCCESS
          ? EStepState.SUCCESS
          : step === steps.ERROR
            ? EStepState.ERROR
            : EStepState.DISABLED,
    },
  ] : [
    {
      text: t("fbpPage.matchings.step-1"),
      state: step === steps.SUCCESS
        ? EStepState.SUCCESS
        : EStepState.ENABLED ,
    },
      ...(marketplacesAvailable.length === 1 ? [] : [
        {
          text: t("fbpPage.matchings.step-2"),
          state: (step === steps.FORM) ? (
            accordeonPage === "marketplaces" ? EStepState.DISABLED : EStepState.ENABLED
          ) : EStepState.SUCCESS,
        }
      ]),
    {
      text: t("fbpPage.matchings.step-3"),
      state:
        step === steps.SUCCESS
          ? EStepState.SUCCESS
          : step === steps.ERROR
            ? EStepState.ERROR
            : EStepState.DISABLED,
    },
  ];

  const stepper = <Stepper steps={stepsData} gap={(matchingData || marketplacesAvailable.length === 1) ? "small" : undefined} />;

  if (step === steps.SUCCESS) {
    return (
      <Modal
        isOpen={true}
        title={title}
        headerContent={stepper}
        size={ModalSize.L}
        onClose={onClose}
        footerButtons={[
          {
            title: t("fbpPage.matchings.cancelBtn"),
            onClick: onClose,
          },
        ]}
      >
        <MessageModalContent
          title={t("fbpPage.matchings.crateMatchingSuccessTitle")}
          description={t("fbpPage.matchings.crateMatchingSuccessDescription")}
          variant="success"
        />
      </Modal>
    );
  }

  if (step === steps.ERROR) {
    return (
      <Modal
        isOpen={true}
        title={title}
        headerContent={stepper}
        size={ModalSize.L}
        onClose={onClose}
        footerButtons={[
          {
            title: t("fbpPage.matchings.cancelBtn"),
            onClick: onClose,
          },
          {
            title: t("fbpPage.matchings.errorRepeateBtn"),
            onClick: () => setStep(steps.FORM),
          },
        ]}
      >
        <MessageModalContent
          title={t("fbpPage.matchings.crateMatchingErrorTitle")}
          description={t("fbpPage.matchings.crateMatchingErrorDescription")}
          variant="warning"
        />
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={true}
      title={title}
      headerContent={stepper}
      headerTitleContent={
        buttonProps.alert ? (
          <div className={styles.validationAlert}>
            {buttonProps.alert}
          </div>
        ) : null
      }
      size={ModalSize.L}
      className={styles.wideModal}
      onClose={onClose}
      confirmCancel
      // confirmCancelDescription={t('fbpPage.createOrder.closeConfirmDescription')}
      footerButtons={[
        matchingData
          ? {
              title: t("fbpPage.matchings.removeBtn"),
              onClick: () => {
                checkDelMutation.mutate();
              },
              loading: checkDelMutation.isLoading,
            }
          : {
              title: t("fbpPage.matchings.cancelBtn"),
            },
        {
          title: t(
            matchingData
              ? "fbpPage.matchings.saveBtn"
              : "fbpPage.matchings.createBtn",
          ),
          onClick: () => {
            formRef.current && formRef.current.submit();
          },
          disabled: buttonProps.disabled,
          loading: buttonProps.isLoading,
        },
      ]}
    >
      <Form
        formRef={formRef}
        sellerWarehouseId={sellerWarehouseId}
        hasAliStores={hasAliStores}
        hasWbStores={hasWbStores}
        hasYmStores={hasYmStores}
        matchingData={matchingData}
        onStatusChange={handleStatusChange}
        onSuccess={onSuccess}
        onError={onError}
        setAccordeonPage={setAccordeonPage}
        accordeonPage={accordeonPage}
      />

      {isOpenDelWindow && (
        <ConfirmModal
          title={t("fbpPage.removeMatchingModal.title")}
          description={t("fbpPage.removeMatchingModal.description")}
          noBtnTitle={t("fbpPage.removeMatchingModal.cancelBtn")}
          yesBtnTitle={t("fbpPage.removeMatchingModal.okBtn")}
          onClose={() => setIsOpenDelWindow(false)}
          requestFn={() => delMutation.mutate(matchingId)}
          onSuccess={() => {
            setIsOpenDelWindow(false);
            onClose();
          }}
          icon={<DelIcon2 />}
          inModal
        />
      )}
    </Modal>
  );
};

export default MatchingModal;
